import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { environment } from '../../../projects/my-app/src/environments/environment';

interface MarketPositioningBreadcrumb {
  label: string;
}

interface MarketPositioningRatingOption {
  label: string;
  value: string;
  color?: string;
  background?: string;
  borderColor?: string;
  activeBackground?: string;
  activeColor?: string;
}

interface MarketPositioningSegmentOption {
  label: string;
  value: string;
}

// Data shape for the history hover-popover, mirroring json-driven-smart-table's — kept
// separate from rendering so a future API response only needs mapping into this shape.
interface MarketPositioningHistoryChange {
  field: string;
  previousValue: string;
  newValue: string;
}

interface MarketPositioningHistoryVersion {
  versionNo: number;
  updatedBy: string;
  updatedDate: string;
  changes: MarketPositioningHistoryChange[];
}

interface MarketPositioningHistoryPopoverConfig {
  customerId: string;
  updatedBy: string;
  updatedDate: string;
  versions: MarketPositioningHistoryVersion[];
}

interface MarketPositioningHistoryFieldGroup {
  keys: string[];
  displayPriority: string[];
}

// Fired on hover — payload shape is fixed per the backend contract (attributesList/
// filterList/entityName), NOT the generic requestData/endpoints envelope used elsewhere
// in this app, so it's sent via a direct HTTP call rather than through ConfigService.
interface MarketPositioningHistoryApiConfig {
  apiUrl?: string;
  method?: string;
  entityName: string;
  attributesList?: string[];
  filterAttributeName?: string;
  filterOperator?: string;
  // When true, re-sends the row's current rating status through the same update endpoint
  // (Customer_Planning_X_Axis_Update_Service) before fetching history — some backends only
  // append a history record when this endpoint is called, so a plain read-only history fetch
  // can otherwise miss the latest state.
  syncBeforeFetch?: boolean;
}

interface MarketPositioningColumn {
  header: string;
  field: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  visible?: boolean;
  editable?: boolean;
  type?: 'label' | 'rating' | 'history';
  sortDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  // 'history' column config — icon-only cell showing a hover popover of past changes.
  historyIcon?: string;
  historyPopoverTitleField?: string;
  historyApi?: MarketPositioningHistoryApiConfig;
}

interface MarketPositioningCell {
  rating: string;
  confidence?: number | null;
  update?: string | null;
  reason?: string | null;
}

interface MarketPositioningRow {
  [key: string]: string | MarketPositioningCell;
  id: string;
  segment: string;
}

interface MarketPositioningDataFile {
  segments?: MarketPositioningSegmentOption[];
  rows?: MarketPositioningRow[];
}

interface MarketPositioningApiDriver {
  label?: string;
  status?: string;
  percentage?: string | number | null;
  confidence?: number | null;
  update?: string | null;
  reason?: string | null;
  [key: string]: any;
}

interface MarketPositioningApiItem {
  customer_name?: string;
  segment?: string;
  xAxisDrivers?: MarketPositioningApiDriver[];
  yAxisDrivers?: MarketPositioningApiDriver[];
  [key: string]: any;
}

interface MarketPositioningLegendMoreInfoAxisSection {
  label: string;
  tone?: 'external' | 'internal' | string;
  title: string;
  subtitle: string;
}

interface MarketPositioningLegendMoreInfoRow {
  factor: string;
  source: string;
  weak: string;
  fair: string;
  good: string;
  customerValue: string;
  customerScore: string;
  axis?: string;
}

interface MarketPositioningLegendMoreInfoStructured {
  exampleTitle?: string;
  axisSections?: MarketPositioningLegendMoreInfoAxisSection[];
  rows?: MarketPositioningLegendMoreInfoRow[];
  note?: string;
}

@Component({
  selector: 'app-market-positioning',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './market-positioning.component.html',
  styleUrls: ['./market-positioning.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketPositioningComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly SAVED_RATINGS_STORAGE_PREFIX = 'dvm.marketPositioning.savedRatings';
  @Input() data: any;
  @Output() rowsChanged = new EventEmitter<MarketPositioningRow[]>();

  rows: MarketPositioningRow[] = [];
  private originalRows: MarketPositioningRow[] = [];
  private loadSub?: Subscription;
  private filterOptionsSub?: Subscription;
  private globalFilterSub?: Subscription;
  private readonly pendingCellChanges = new Set<string>();
  private readonly savingCells = new Set<string>();
  segmentOptions: MarketPositioningSegmentOption[] = [];
  selectedRating = '';
  selectedSegment = '';
  segmentSearchTerm = '';
  isSegmentDropdownOpen = false;
  openRatingEditorCellKey: string | null = null;
  openReasonEditorCellKey: string | null = null;
  editReasons: Record<string, string> = {};
  savedEditReasons: Record<string, string> = {};
  updatedCellKeys = new Set<string>();
  hoverPopoverCellKey: string | null = null;
  pinnedHistoryPopoverCellKey: string | null = null;
  hoverPopoverPosition: { top: number; right: number } | null = null;
  hoverPopoverMaxHeight: number | null = null;
  private collapsedHistoryVersionKeys = new Set<string>();
  private historyApiCache = new Map<string, MarketPositioningHistoryPopoverConfig>();
  private historyApiLoading = new Set<string>();
  loading = false;
  errorMessage = '';
  successMessage = '';
  private successMessageTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef,
    private readonly configService: ConfigService,
    private readonly globalFilterService: GlobalFilterService,
    private readonly keycloakService: KeycloakService,
  ) {}

  // Same token-parsing pattern as json-driven-smart-table's getCurrentUserContext() —
  // resolves the actually logged-in user so update payloads (and, downstream, history
  // audit records) are attributed correctly instead of carrying no identity at all.
  private getCurrentUsername(): string {
    const user = this.getCurrentUserContext();
    return user.username || user.email || user.userId;
  }

  private getCurrentUserContext(): { userId: string; username: string; email: string } {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const email = this.firstString(
      tokenParsed?.email,
      typeof tokenParsed?.preferred_username === 'string' && tokenParsed.preferred_username.includes('@')
        ? tokenParsed.preferred_username
        : null,
    );

    return {
      userId: this.firstString(tokenParsed?.sub, keycloak?.subject),
      username: this.firstString(
        tokenParsed?.preferred_username,
        tokenParsed?.name,
        email,
      ),
      email,
    };
  }

  private firstString(...candidates: any[]): string {
    for (const candidate of candidates) {
      const value = String(candidate ?? '').trim();
      if (value) {
        return value;
      }
    }
    return '';
  }

  ngOnInit(): void {
    this.initializeFromConfig();
    this.globalFilterSub?.unsubscribe();
    this.globalFilterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (filters?.skipReload) {
        return;
      }

      this.loadData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.initializeFromConfig();
    }
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.filterOptionsSub?.unsubscribe();
    this.globalFilterSub?.unsubscribe();
    this.clearSuccessMessageTimer();
  }

  get pageTitle(): string {
    return String(this.data?.title || '');
  }

  get pageSubtitle(): string {
    return String(this.data?.subtitle || '');
  }

  get breadcrumb(): MarketPositioningBreadcrumb[] {
    return Array.isArray(this.data?.breadcrumb) ? this.data.breadcrumb : [];
  }

  get hasHeaderContent(): boolean {
    return Boolean(this.pageTitle.trim() || this.pageSubtitle.trim() || this.breadcrumb.length);
  }

  get ratingFilters(): MarketPositioningRatingOption[] {
    return Array.isArray(this.data?.filters?.rating?.options) ? this.data.filters.rating.options : [];
  }

  get defaultRatingFilter(): string {
    return String(this.data?.filters?.rating?.defaultValue ?? this.ratingFilters[0]?.value ?? '');
  }

  get defaultSegmentFilter(): string {
    return String(this.data?.filters?.segment?.defaultValue ?? this.segmentOptions[0]?.value ?? '');
  }

  get segmentPlaceholder(): string {
    return String(this.data?.filters?.segment?.placeholder || '');
  }

  get showSegmentFilter(): boolean {
    return this.data?.filters?.segment?.visible !== false && this.segmentOptions.length > 0;
  }

  get helperText(): string {
    return String(this.data?.helperText || '');
  }

  get legendTitle(): string {
    return String(this.data?.legend?.title || '');
  }

  get legendItems(): string[] {
    return Array.isArray(this.data?.legend?.items) ? this.data.legend.items : [];
  }

  get legendMoreInfoLabel(): string {
    return String(this.data?.legend?.moreInfo?.label || 'More Info');
  }

  get legendMoreInfoImageSrc(): string {
    return String(this.data?.legend?.moreInfo?.imageSrc || '');
  }

  get legendMoreInfoAlt(): string {
    return String(this.data?.legend?.moreInfo?.alt || 'Market positioning legend reference');
  }

  get legendMoreInfoStructured(): MarketPositioningLegendMoreInfoStructured | null {
    const structured = this.data?.legend?.moreInfo?.structured;
    return structured && typeof structured === 'object' ? structured : null;
  }

  get legendMoreInfoRows(): MarketPositioningLegendMoreInfoRow[] {
    return Array.isArray(this.legendMoreInfoStructured?.rows) ? this.legendMoreInfoStructured?.rows : [];
  }

  get legendMoreInfoAxisSections(): MarketPositioningLegendMoreInfoAxisSection[] {
    return Array.isArray(this.legendMoreInfoStructured?.axisSections)
      ? this.legendMoreInfoStructured?.axisSections
      : [];
  }

  get legendMoreInfoRowsWithAxisSpan(): Array<
    MarketPositioningLegendMoreInfoRow & {
      axisSection?: MarketPositioningLegendMoreInfoAxisSection;
      axisRowSpan?: number;
    }
  > {
    const rows = this.legendMoreInfoRows;
    const sections = this.legendMoreInfoAxisSections;
    const result = rows.map((row) => ({ ...row })) as Array<
      MarketPositioningLegendMoreInfoRow & {
        axisSection?: MarketPositioningLegendMoreInfoAxisSection;
        axisRowSpan?: number;
      }
    >;

    let sectionIndex = -1;
    let groupStartIndex = -1;
    for (let i = 0; i < result.length; i++) {
      if (result[i].axis) {
        if (groupStartIndex >= 0) {
          result[groupStartIndex].axisRowSpan = i - groupStartIndex;
        }
        sectionIndex++;
        result[i].axisSection = sections[sectionIndex];
        groupStartIndex = i;
      }
    }
    if (groupStartIndex >= 0) {
      result[groupStartIndex].axisRowSpan = result.length - groupStartIndex;
    }

    return result;
  }

  get legendMoreInfoNote(): string {
    return String(this.legendMoreInfoStructured?.note || '');
  }

  get legendMoreInfoExampleTitle(): string {
    return String(this.legendMoreInfoStructured?.exampleTitle || '');
  }

  get legendMoreInfoPreviewStyle(): Record<string, string> {
    const configuredWidth = String(this.data?.legend?.moreInfo?.popupWidth || '').trim();
    const configuredMaxHeight = String(this.data?.legend?.moreInfo?.popupMaxHeight || '').trim();

    return {
      width: configuredWidth || '',
      maxHeight: configuredMaxHeight || '',
    };
  }

  get hasStructuredLegendMoreInfo(): boolean {
    return this.legendMoreInfoRows.length > 0;
  }

  get hasLegendMoreInfo(): boolean {
    return this.hasStructuredLegendMoreInfo || !!this.legendMoreInfoImageSrc.trim();
  }

  get visibleColumns(): MarketPositioningColumn[] {
    const columns = Array.isArray(this.data?.table?.columns) ? this.data.table.columns : [];
    return columns.filter((column: MarketPositioningColumn) => column.visible !== false);
  }

  get tableShellStyle(): Record<string, string> {
    const configuredHeight = String(this.data?.table?.containerHeight || this.data?.table?.height || '').trim();
    const configuredMaxHeight = String(this.data?.table?.maxHeight || '').trim();

    return {
      height: configuredHeight || '',
      maxHeight: configuredMaxHeight || configuredHeight || '',
    };
  }

  get segmentFilterStyle(): Record<string, string> {
    const filterConfig = this.data?.filters?.segment || {};
    const configuredWidth = String(filterConfig?.dropdownWidth || filterConfig?.width || '').trim();
    const configuredMaxWidth = String(filterConfig?.maxWidth || '').trim();

    return {
      width: configuredWidth || '',
      maxWidth: configuredMaxWidth || configuredWidth || '',
    };
  }

  get filteredRows(): MarketPositioningRow[] {
    return this.rows.filter((row) => {
      const segmentMatches = this.matchesTopFilter(row);
      return segmentMatches;
    });
  }

  get resetLabel(): string {
    return String(this.data?.actions?.reset?.label || '');
  }

  get hasResetAction(): boolean {
    return !!(this.resetLabel || this.resetIcon);
  }

  get resetIcon(): string {
    return String(this.data?.actions?.reset?.icon || '');
  }

  get exportLabel(): string {
    return String(this.data?.actions?.export?.label || '');
  }

  get saveLabel(): string {
    return String(this.data?.actions?.save?.label || '');
  }

  get hasSaveAction(): boolean {
    return !!(this.saveLabel || this.saveIcon);
  }

  get saveIcon(): string {
    return String(this.data?.actions?.save?.icon || '');
  }

  get isSaveActionDisabled(): boolean {
    return this.pendingCellChanges.size === 0 || this.savingCells.size > 0;
  }

  get hasExportAction(): boolean {
    return !!(this.exportLabel || this.exportIcon);
  }

  get exportIcon(): string {
    return String(this.data?.actions?.export?.icon || '');
  }

  get selectedSegmentLabel(): string {
    return this.segmentOptions.find((option) => option.value === this.selectedSegment)?.label || this.segmentPlaceholder;
  }

  get segmentSearchPlaceholder(): string {
    return String(this.data?.filters?.segment?.searchPlaceholder || 'Search customer');
  }

  get filteredSegmentOptions(): MarketPositioningSegmentOption[] {
    const term = this.normalizeOptionSearch(this.segmentSearchTerm);
    if (!term) {
      return this.segmentOptions;
    }

    return this.segmentOptions.filter((option) => {
      if (this.includeAllSegmentOption && option.value === this.allSegmentValue) {
        return true;
      }

      return this.normalizeOptionSearch(option.label).includes(term);
    });
  }

  private get allRatingValue(): string {
    return String(this.data?.filters?.rating?.allValue ?? 'all');
  }

  private get allSegmentValue(): string {
    return String(this.data?.filters?.segment?.allValue ?? 'all');
  }

  private get includeAllSegmentOption(): boolean {
    return this.data?.filters?.segment?.includeAllOption !== false;
  }

  private initializeFromConfig(): void {
    this.selectedRating = this.defaultRatingFilter;
    this.selectedSegment = this.defaultSegmentFilter;
    if (this.showSegmentFilter) {
      this.loadSegmentOptions();
    } else {
      this.segmentOptions = [];
      this.selectedSegment = '';
      this.isSegmentDropdownOpen = false;
      this.segmentSearchTerm = '';
    }
    this.loadData();
  }

  loadData(): void {
    this.loadSub?.unsubscribe();

    const apiUrl = String(this.data?.apiUrl || '').trim();
    if (apiUrl) {
      this.loading = true;
      this.errorMessage = '';
      const currentFilters = this.buildTableFilters();
      const requestConfig = this.buildTableRequestConfig();
      this.loadSub = this.configService
        .getConfig(apiUrl, requestConfig, currentFilters, requestConfig?.method || this.data?.method || 'post')
        .subscribe({
          next: (response) => {
            this.applyApiResponse(response);
            this.loading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.errorMessage = String(this.data?.messages?.loadError || '');
            this.cdr.markForCheck();
          },
        });
      return;
    }

    const dataUrl = String(this.data?.dataUrl || '').trim();
    if (!dataUrl) {
      this.applyDataFile({ rows: [], segments: [] });
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.http.get<MarketPositioningDataFile>(dataUrl).subscribe({
      next: (response) => {
        this.applyDataFile(response || {});
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = String(this.data?.messages?.loadError || '');
        this.cdr.markForCheck();
      },
    });
  }

  selectRating(value: string): void {
    this.selectedRating = value;
    this.clearSuccessMessage();
    this.cdr.markForCheck();
  }

  onSegmentChange(): void {
    if (!this.showSegmentFilter) {
      return;
    }
    this.loadData();
  }

  toggleSegmentDropdown(event?: Event): void {
    if (!this.showSegmentFilter) {
      return;
    }
    event?.stopPropagation();
    this.isSegmentDropdownOpen = !this.isSegmentDropdownOpen;
    if (!this.isSegmentDropdownOpen) {
      this.segmentSearchTerm = '';
    }
  }

  isRatingEditorOpen(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.openRatingEditorCellKey === this.getCellKey(row, column);
  }

  toggleRatingEditor(row: MarketPositioningRow, column: MarketPositioningColumn, event: Event): void {
    event.stopPropagation();
    const cellKey = this.getCellKey(row, column);
    if (this.openRatingEditorCellKey === cellKey) {
      this.openRatingEditorCellKey = null;
      return;
    }

    this.openRatingEditorCellKey = cellKey;
  }

  chooseRating(row: MarketPositioningRow, column: MarketPositioningColumn, value: string, event: Event): void {
    event.stopPropagation();
    const cell = this.getCell(row, column);
    if (!cell) {
      return;
    }

    cell.rating = value;
    this.updatePendingState(row, column, cell);
    this.clearSuccessMessage();
    this.openRatingEditorCellKey = null;
    this.emitRows();
  }

  shouldShowReasonButton(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    const cell = this.getCell(row, column);
    return column.editable === true
      && column.type !== 'label'
      && this.isCellDirty(row, column)
      && this.isEditableRatingValue(cell?.rating);
  }

  isReasonEditorOpen(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.openReasonEditorCellKey === this.getCellKey(row, column);
  }

  toggleReasonEditor(row: MarketPositioningRow, column: MarketPositioningColumn, event: Event): void {
    event.stopPropagation();
    const cellKey = this.getCellKey(row, column);
    this.openReasonEditorCellKey = this.openReasonEditorCellKey === cellKey ? null : cellKey;
  }

  getReasonIcon(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    return this.isReasonEditorOpen(row, column)
      ? String(this.data?.editReason?.collapseIcon || '-')
      : String(this.data?.editReason?.expandIcon || '+');
  }

  getEditReason(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    return this.editReasons[this.getCellKey(row, column)] || '';
  }

  getSavedEditReason(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    const cellKey = this.getCellKey(row, column);
    return this.savedEditReasons[cellKey] || this.getDisplayText(this.getCell(row, column)?.reason);
  }

  setEditReason(row: MarketPositioningRow, column: MarketPositioningColumn, value: string): void {
    this.editReasons[this.getCellKey(row, column)] = String(value ?? '');
  }

  isCellUpdated(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.updatedCellKeys.has(this.getCellKey(row, column))
      || !!this.getCellUpdateLabel(row, column)
      || !!this.getSavedEditReason(row, column);
  }

  getCellUpdateLabel(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    return this.formatDisplayLabel(this.getCell(row, column)?.update);
  }

  onReasonKeydown(row: MarketPositioningRow, column: MarketPositioningColumn, event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    this.openReasonEditorCellKey = null;
    this.cdr.markForCheck();
  }

  selectSegmentOption(value: string): void {
    if (!this.showSegmentFilter) {
      return;
    }
    this.selectedSegment = value;
    this.isSegmentDropdownOpen = false;
    this.segmentSearchTerm = '';
    this.loadData();
  }

  trackByOptionValue(_index: number, option: MarketPositioningSegmentOption): string {
    return option.value;
  }

  @HostListener('document:click')
  closeSegmentDropdown(): void {
    this.isSegmentDropdownOpen = false;
    this.segmentSearchTerm = '';
    this.openRatingEditorCellKey = null;
    this.openReasonEditorCellKey = null;
  }

  reset(): void {
    this.pendingCellChanges.clear();
    this.savingCells.clear();
    this.clearSuccessMessage();
    this.selectedRating = this.defaultRatingFilter;
    this.selectedSegment = this.showSegmentFilter ? this.defaultSegmentFilter : '';
    this.isSegmentDropdownOpen = false;
    this.openRatingEditorCellKey = null;
    this.openReasonEditorCellKey = null;

    if (String(this.data?.apiUrl || '').trim()) {
      this.loadData();
      return;
    }

    this.rows = this.cloneRows(this.originalRows);
    this.editReasons = {};
    this.savedEditReasons = {};
    this.updatedCellKeys.clear();
    this.emitRows();
  }

  export(): void {
    const columns = this.visibleColumns;
    const rows = this.filteredRows;
    const csv = this.buildCsv(columns, rows);
    const fileName = this.resolveExportFileName();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  saveAllChanges(): void {
    if (this.isSaveActionDisabled) {
      return;
    }

    this.clearSuccessMessage();

    for (const row of this.rows) {
      for (const column of this.visibleColumns) {
        if (!column.editable || column.type === 'label') {
          continue;
        }

        if (this.isCellDirty(row, column) && !this.isCellSaving(row, column)) {
          this.saveCell(row, column);
        }
      }
    }
  }

  cycleRating(row: MarketPositioningRow, column: MarketPositioningColumn): void {
    if (!column.editable || column.type === 'label') {
      return;
    }

    const cell = this.getCell(row, column);
    const order = this.ratingCycle;
    if (!cell || order.length === 0) {
      return;
    }

    const currentIndex = order.findIndex((value) => value === cell.rating);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % order.length : 0;
    cell.rating = order[nextIndex];
    this.updatePendingState(row, column, cell);
    this.emitRows();
  }

  saveCell(row: MarketPositioningRow, column: MarketPositioningColumn): void {
    if (!this.isCellDirty(row, column) || this.isCellSaving(row, column)) {
      return;
    }

    const updateConfig = this.data?.updateAction;
    const apiUrl = String(updateConfig?.apiUrl || this.data?.apiUrl || '').trim();
    const cell = this.getCell(row, column);
    if (!apiUrl || !cell) {
      return;
    }

    const pendingUpdates = this.collectPendingUpdates();
    if (pendingUpdates.length === 0) {
      return;
    }

    const pendingKeys = pendingUpdates.map((item) => item.cellKey);
    const payload = this.buildUpdatePayload(pendingUpdates);
    pendingKeys.forEach((key) => this.savingCells.add(key));
    this.errorMessage = '';
    this.clearSuccessMessage();
    this.cdr.markForCheck();

    this.configService
      .getApi(
        apiUrl,
        { queryParams: {}, requestParams: {} },
        {
          skipDefaultPayload: true,
          customPayload: payload,
          headers: updateConfig?.headers,
        },
        updateConfig?.method || 'post',
      )
      .subscribe({
        next: () => {
          this.commitSavedCells(pendingUpdates);
          pendingKeys.forEach((key) => this.savingCells.delete(key));
          this.showSuccessMessage();
          this.invalidateHistoryCacheForRows(pendingUpdates.map((update) => update.row));
          if (updateConfig?.refreshAfterSave === true) {
            this.loadData();
            return;
          }
          this.emitRows();
        },
        error: () => {
          pendingKeys.forEach((key) => this.savingCells.delete(key));
          this.clearSuccessMessage();
          this.errorMessage = String(this.data?.messages?.updateError || this.data?.messages?.loadError || '');
          this.cdr.markForCheck();
        },
      });
  }

  getCell(row: MarketPositioningRow, column: MarketPositioningColumn): MarketPositioningCell | null {
    const value = row[column.field];
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as MarketPositioningCell
      : null;
  }

  getCellLabel(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    return String(row[column.field] ?? '');
  }

  getRatingConfig(value: string): MarketPositioningRatingOption {
    return this.ratingFilters.find((option) => option.value === value) || { label: value, value };
  }

  get ratingEditorOptions(): MarketPositioningRatingOption[] {
    const configuredCycle = Array.isArray(this.data?.editBehavior?.cycle)
      ? this.data.editBehavior.cycle.map((value: any) => String(value))
      : [];

    if (configuredCycle.length > 0) {
      return configuredCycle.map((value: string) => this.getRatingConfig(value));
    }

    return this.ratingFilters.filter((option) => option.value !== this.allRatingValue);
  }

  private isEditableRatingValue(value: any): boolean {
    const normalizedValue = this.normalizeKey(value);
    if (!normalizedValue) {
      return false;
    }

    return this.ratingEditorOptions.some((option) => this.normalizeKey(option.value) === normalizedValue);
  }

  getRatingOptionStyle(option: MarketPositioningRatingOption, currentRating: string): Record<string, string> {
    const active = option.value === currentRating;
    return {
      color: option.color || (active ? '#000' : '#3e4a59'),
      background: active ? option.activeBackground || option.background || '#eef1f5' : option.background || '#fff',
      borderColor: active ? option.borderColor || option.color || '#d6dfe7' : option.borderColor || '#d6dfe7',
    };
  }

  getBadgeStyle(value: string): Record<string, string> {
    const config = this.getRatingConfig(value);
    return {
      color: config.color || '',
      background: config.background || '',
      borderColor: config.borderColor || '',
    };
  }

  getFilterStyle(option: MarketPositioningRatingOption): Record<string, string> {
    const isActive = this.selectedRating === option.value;
    return {
      color: isActive ? option.activeColor || option.color || '' : option.color || '',
      background: isActive ? option.activeBackground || option.background || '' : option.background || '',
      borderColor: option.borderColor || 'transparent',
    };
  }

  getProgressStyle(cell: MarketPositioningCell): Record<string, string> {
    const progress = Math.max(0, Math.min(Number(cell?.confidence || 0), 100));
    const color = this.getRatingConfig(cell?.rating).color || this.data?.table?.progress?.color || '';
    return {
      width: `${progress}%`,
      background: color,
    };
  }

  shouldShowProgress(cell: MarketPositioningCell | null): boolean {
    if (this.data?.table?.progress?.visible === false) {
      return false;
    }

    if (!cell) {
      return false;
    }

    return Number.isFinite(Number(cell.confidence));
  }

  getColumnStyle(column: MarketPositioningColumn): Record<string, string> {
    return {
      width: column.width || 'auto',
      textAlign: column.align || 'left',
    };
  }

  getCellContentStyle(column: MarketPositioningColumn): Record<string, string> {
    const align = column.align || 'left';

    if (align === 'center') {
      return {
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
      };
    }

    if (align === 'right') {
      return {
        marginLeft: 'auto',
        marginRight: '0',
        textAlign: 'right',
      };
    }

    return {
      marginLeft: '0',
      marginRight: 'auto',
      textAlign: 'left',
    };
  }

  isCellDirty(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.pendingCellChanges.has(this.getCellKey(row, column));
  }

  isCellSaving(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.savingCells.has(this.getCellKey(row, column));
  }

  private get ratingCycle(): string[] {
    const configured = this.data?.editBehavior?.cycle;
    if (Array.isArray(configured) && configured.length > 0) {
      return configured.map((value: any) => String(value));
    }
    return this.ratingFilters.map((option) => option.value).filter((value) => value !== this.allRatingValue);
  }

  private applyDataFile(response: MarketPositioningDataFile): void {
    if (!this.segmentOptions.length) {
      this.segmentOptions = Array.isArray(response.segments) ? response.segments : [];
    }
    const savedRows = this.applySavedRatings(this.cloneRows(response.rows || []));
    this.originalRows = this.sortRows(savedRows);
    this.rows = this.cloneRows(this.originalRows);
    this.pendingCellChanges.clear();
    this.savingCells.clear();
    this.editReasons = {};
    this.savedEditReasons = {};
    this.updatedCellKeys.clear();
    this.openReasonEditorCellKey = null;
    this.ensureSegmentOptions(this.rows);
    if (!this.selectedSegment) {
      this.selectedSegment = this.defaultSegmentFilter;
    }
  }

  private applyApiResponse(response: any): void {
    const items = this.extractApiItems(response);
    const mappedRows = items.map((item, index) => this.mapApiItemToRow(item, index));
    const savedRows = this.applySavedRatings(this.cloneRows(mappedRows));
    this.originalRows = this.sortRows(savedRows);
    this.rows = this.cloneRows(this.originalRows);
    this.pendingCellChanges.clear();
    this.savingCells.clear();
    this.editReasons = {};
    this.savedEditReasons = {};
    this.updatedCellKeys.clear();
    this.openReasonEditorCellKey = null;
    this.ensureCustomerOptionsFromItems(items);
    this.ensureSegmentOptions(this.rows);
    if (!this.selectedSegment || !this.segmentOptions.some((option) => option.value === this.selectedSegment)) {
      this.selectedSegment = this.defaultSegmentFilter;
    }
  }

  private extractApiItems(response: any): MarketPositioningApiItem[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response && typeof response === 'object' && (response.customer_name || response.xAxisDrivers || response.yAxisDrivers)) {
      return [response as MarketPositioningApiItem];
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.responseList?.[0]?.data)) {
      return response.responseList[0].data;
    }

    return [];
  }

  private mapApiItemToRow(item: MarketPositioningApiItem, index: number): MarketPositioningRow {
    const labelField = String(this.data?.apiMapping?.rowLabelField || 'customer_name');
    const segmentField = String(this.data?.apiMapping?.segmentField || 'segment');
    const ratingLabelMap = this.toPlainObject(this.data?.apiMapping?.columnLabelMap);
    const rowLabelColumn = this.visibleColumns.find((column) => column.type === 'label');
    const rowLabel = String(this.resolvePath(item, labelField) ?? `Row ${index + 1}`);
    const segmentValue = String(this.resolvePath(item, segmentField) ?? this.allSegmentValue);
    const driverLookup = this.buildDriverLookup(item);
    const primitiveFields = this.extractPrimitiveItemFields(item);

    const row: MarketPositioningRow = {
      ...primitiveFields,
      id: this.buildRowId(rowLabel, index),
      segment: segmentValue || this.allSegmentValue,
      customer_name: rowLabel,
      [rowLabelColumn?.field || 'emSegment']: rowLabel,
    };

    this.visibleColumns
      .filter((column) => column.type !== 'label')
      .forEach((column) => {
        const configuredLabel = ratingLabelMap[column.field] || column.header;
        const driver = driverLookup.get(this.normalizeKey(configuredLabel));
        row[column.field] = {
          rating: this.normalizeRating(driver?.status),
          confidence: this.toConfidenceValue(
            driver?.percentage ??
            driver?.confidence,
          ),
          update: this.getDisplayText(driver?.update) || null,
          reason: this.getDisplayText(driver?.reason) || null,
        };
      });

    return row;
  }

  private extractPrimitiveItemFields(item: MarketPositioningApiItem): Record<string, any> {
    return Object.entries(item || {}).reduce<Record<string, any>>((acc, [key, value]) => {
      if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});
  }

  private buildDriverLookup(item: MarketPositioningApiItem): Map<string, MarketPositioningApiDriver> {
    const collections = Array.isArray(this.data?.apiMapping?.driverCollections)
      ? this.data.apiMapping.driverCollections
      : ['xAxisDrivers', 'yAxisDrivers'];
    const labelKey = String(this.data?.apiMapping?.labelField || 'label');
    const lookup = new Map<string, MarketPositioningApiDriver>();

    collections.forEach((collectionName: string) => {
      const drivers = this.resolvePath(item, collectionName);
      if (!Array.isArray(drivers)) {
        return;
      }

      drivers.forEach((driver: MarketPositioningApiDriver) => {
        const label = String(driver?.[labelKey as keyof MarketPositioningApiDriver] || '');
        if (label) {
          lookup.set(this.normalizeKey(label), driver);
        }
      });
    });

    return lookup;
  }

  private ensureSegmentOptions(rows: MarketPositioningRow[]): void {
    if (this.segmentOptions.length > 1) {
      return;
    }
    const uniqueSegments = Array.from(
      new Set(
        rows
          .map((row) => String(row.segment || '').trim())
          .filter((segment) => !!segment && segment !== this.allSegmentValue),
      ),
    );

    this.segmentOptions = this.buildAllOptionList(
      uniqueSegments.map((segment) => ({
        label: segment,
        value: segment,
      })),
    );
  }

  private ensureCustomerOptionsFromItems(items: MarketPositioningApiItem[]): void {
    const customerOptions = items
      .map((item) => String(item?.customer_name || '').trim())
      .filter((value) => !!value)
      .map((value) => ({
        label: value,
        value,
      }));

    if (customerOptions.length === 0) {
      return;
    }

    this.segmentOptions = this.buildAllOptionList(customerOptions);
  }

  private loadSegmentOptions(): void {
    if (!this.showSegmentFilter) {
      this.segmentOptions = [];
      return;
    }

    const filterConfig = this.data?.filters?.segment;
    const apiUrl = String(filterConfig?.apiUrl || '').trim();
    if (!apiUrl) {
      return;
    }

    this.filterOptionsSub?.unsubscribe();
    const requestConfig = {
      apiUrl,
      method: filterConfig?.method || 'post',
      functionName: filterConfig?.functionName,
      version: filterConfig?.version || '1.0',
      entity: filterConfig?.entity || 'default_entity',
      workflowId: filterConfig?.workflowId || 'default_workflow',
      defaultRequestData: this.toPlainObject(filterConfig?.defaultRequestData),
      requestFilterKeys: Array.isArray(filterConfig?.requestFilterKeys) ? filterConfig.requestFilterKeys : undefined,
      excludeFilterKeys: Array.isArray(filterConfig?.excludeFilterKeys) ? filterConfig.excludeFilterKeys : undefined,
      headers: filterConfig?.headers,
    };

    this.filterOptionsSub = this.configService
      .getConfig(apiUrl, requestConfig, this.globalFilterService.getCurrentFilters(), requestConfig.method)
      .subscribe({
        next: (response) => {
          this.segmentOptions = this.normalizeSegmentOptions(response);
          if (!this.segmentOptions.some((option) => option.value === this.selectedSegment)) {
            this.selectedSegment = this.defaultSegmentFilter;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.segmentOptions = this.segmentOptions.length ? this.segmentOptions : this.buildAllOptionList([]);
          this.cdr.markForCheck();
        },
      });
  }

  private normalizeSegmentOptions(response: any): MarketPositioningSegmentOption[] {
    const filterConfig = this.data?.filters?.segment || {};
    const optionsKey = String(filterConfig?.optionsKey || filterConfig?.responseKey || 'customer_name');
    const labelKey = String(filterConfig?.labelKey || 'label');
    const valueKey = String(filterConfig?.valueKey || 'value');
    const configuredResponsePath = String(filterConfig?.responsePath || '').trim();
    const source =
      (configuredResponsePath ? this.resolvePath(response, configuredResponsePath) : undefined) ??
      response?.[optionsKey] ??
      response;
    const allOptionList = this.buildAllOptionList([]);

    const normalizedSource = Array.isArray(source)
      ? source
      : this.extractApiItems(response);

    if (!Array.isArray(normalizedSource) || normalizedSource.length === 0) {
      return allOptionList;
    }

    const dynamicOptions = normalizedSource
      .map((item: any) => {
        if (typeof item === 'string' || typeof item === 'number') {
          const value = String(item).trim();
          return value ? { label: value, value } : null;
        }

        if (item && typeof item === 'object') {
          const value = String(item[valueKey] ?? item[optionsKey] ?? item[labelKey] ?? '').trim();
          const label = String(item[labelKey] ?? item[valueKey] ?? item[optionsKey] ?? '').trim();
          return value ? { label: label || value, value } : null;
        }

        return null;
      })
      .filter((option: MarketPositioningSegmentOption | null): option is MarketPositioningSegmentOption => !!option);

    if (dynamicOptions.length === 0) {
      const fallbackValues = this.collectStringValuesByKey(response, optionsKey);
      return this.buildAllOptionList(
        fallbackValues.map((value) => ({
          label: value,
          value,
        })),
      );
    }

    return this.buildAllOptionList(dynamicOptions);
  }

  private buildAllOptionList(options: MarketPositioningSegmentOption[]): MarketPositioningSegmentOption[] {
    const uniqueOptions = options
      .filter((option) => option.value !== this.allSegmentValue)
      .filter((option, index, list) => list.findIndex((item) => item.value === option.value) === index);

    if (!this.includeAllSegmentOption) {
      return uniqueOptions;
    }

    const filterConfig = this.data?.filters?.segment || {};
    const allOption = {
      label: String(filterConfig?.placeholder || 'All Segments'),
      value: this.allSegmentValue,
    };

    return [allOption, ...uniqueOptions];
  }

  private collectStringValuesByKey(source: any, targetKey: string): string[] {
    const results = new Set<string>();
    this.walkForKey(source, this.normalizeKey(targetKey), results);
    return Array.from(results);
  }

  private walkForKey(source: any, targetKey: string, results: Set<string>): void {
    if (source === null || source === undefined) {
      return;
    }

    if (Array.isArray(source)) {
      source.forEach((item) => this.walkForKey(item, targetKey, results));
      return;
    }

    if (typeof source !== 'object') {
      return;
    }

    Object.entries(source).forEach(([key, value]) => {
      if (this.normalizeKey(key) === targetKey && (typeof value === 'string' || typeof value === 'number')) {
        const normalizedValue = String(value).trim();
        if (normalizedValue) {
          results.add(normalizedValue);
        }
      }

      this.walkForKey(value, targetKey, results);
    });
  }

  private buildTableFilters(): any {
    const currentFilters = this.globalFilterService.getCurrentFilters() || {};
    return {
      ...currentFilters,
      requestParams: { ...(currentFilters.requestParams || {}) },
      queryParams: { ...(currentFilters.queryParams || {}) },
    };
  }

  private buildTableRequestConfig(): any {
    return this.cloneValue(this.data || {});
  }

  private matchesTopFilter(row: MarketPositioningRow): boolean {
    if (!this.showSegmentFilter) {
      return true;
    }

    if (!this.selectedSegment || this.selectedSegment === this.allSegmentValue) {
      return true;
    }

    const requestKey = String(this.data?.filters?.segment?.requestKey || 'segment');
    const rowValue = row[requestKey];
    if (typeof rowValue === 'string') {
      return rowValue === this.selectedSegment;
    }

    return row.segment === this.selectedSegment;
  }

  private normalizeRating(value: any): string {
    const normalized = this.normalizeKey(value);
    if (!normalized) {
      return this.ratingCycle[0] || 'good';
    }

    const matched = this.ratingFilters.find((option) => this.normalizeKey(option.value) === normalized);
    return matched?.value || String(value || '').toLowerCase();
  }

  private toConfidenceValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const normalizedValue = typeof value === 'string'
      ? value.replace(/%/g, '').trim()
      : value;
    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private buildRowId(label: string, index: number): string {
    const normalized = this.normalizeKey(label).replace(/\s+/g, '-');
    return normalized || `row-${index + 1}`;
  }

  private sortRows(rows: MarketPositioningRow[]): MarketPositioningRow[] {
    const sortConfig = this.resolveSortConfig();
    if (!sortConfig) {
      return rows;
    }

    return [...rows].sort((left, right) => {
      const leftValue = this.getSortableValue(left?.[sortConfig.field]);
      const rightValue = this.getSortableValue(right?.[sortConfig.field]);

      if (leftValue === rightValue) {
        return 0;
      }

      const result = leftValue < rightValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? result : -result;
    });
  }

  private resolveSortConfig(): { field: string; direction: 'asc' | 'desc' } | null {
    const tableSort = this.data?.table?.defaultSort;
    const configuredField = String(tableSort?.field || tableSort?.key || '').trim();
    const configuredDirection = this.normalizeSortDirection(tableSort?.direction || tableSort?.order);
    if (configuredField && configuredDirection) {
      return {
        field: configuredField,
        direction: configuredDirection,
      };
    }

    const columnSort = this.visibleColumns.find((column) => {
      const direction = this.normalizeSortDirection(column?.sortDirection);
      return !!column?.field && !!direction;
    });

    if (!columnSort) {
      return null;
    }

    return {
      field: columnSort.field,
      direction: this.normalizeSortDirection(columnSort.sortDirection) || 'asc',
    };
  }

  private normalizeSortDirection(value: any): 'asc' | 'desc' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'asc' || normalized === 'ascending') {
      return 'asc';
    }

    if (normalized === 'desc' || normalized === 'descending') {
      return 'desc';
    }

    return null;
  }

  private getSortableValue(value: unknown): string | number {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return String((value as MarketPositioningCell)?.rating || '').trim().toLowerCase();
    }

    const raw = String(value ?? '').trim();
    const numeric = Number(raw.replace(/,/g, ''));
    if (raw !== '' && Number.isFinite(numeric)) {
      return numeric;
    }

    return raw.toLowerCase();
  }

  private normalizeKey(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizeOptionSearch(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  private resolvePath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .filter(Boolean)
      .reduce((current, segment) => current?.[segment], source);
  }

  private toPlainObject(value: any): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }

  private resolveUserTemplate(value: any): string {
    const fallback = this.getCurrentUsername();
    const configured = String(value ?? '').trim();
    if (!configured) {
      return fallback;
    }

    const user = this.getCurrentUserContext();
    const resolved = configured.replace(/\{\{\s*currentUser\.(userId|username|email)\s*\}\}/g, (_match, key: keyof typeof user) => {
      return user[key] || '';
    });

    return resolved.trim() || fallback;
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private updatePendingState(row: MarketPositioningRow, column: MarketPositioningColumn, cell: MarketPositioningCell): void {
    const cellKey = this.getCellKey(row, column);
    const originalCell = this.findOriginalCell(row, column);
    if (originalCell?.rating === cell.rating) {
      this.pendingCellChanges.delete(cellKey);
      delete this.editReasons[cellKey];
      if (this.openReasonEditorCellKey === cellKey) {
        this.openReasonEditorCellKey = null;
      }
      return;
    }

    this.pendingCellChanges.add(cellKey);
  }

  private findOriginalCell(row: MarketPositioningRow, column: MarketPositioningColumn): MarketPositioningCell | null {
    const originalRow = this.originalRows.find((item) => item.id === row.id);
    if (!originalRow) {
      return null;
    }

    return this.getCell(originalRow, column);
  }

  private getCellKey(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    return `${row.id}::${column.field}`;
  }

  getHistoryActionLabel(column: MarketPositioningColumn): string {
    return column.historyIcon || "<span class='material-icons'>history</span>";
  }

  isHistoryPopoverActive(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.hoverPopoverCellKey === this.getCellKey(row, column);
  }

  // No longer falls back to column.historyPopover (a static config mock) — the popup must
  // only ever reflect the real API response, or explicitly show "no history" via
  // hasFetchedHistory()/isHistoryLoading() rather than silently showing fabricated data.
  getHistoryPopover(row: MarketPositioningRow, column: MarketPositioningColumn): MarketPositioningHistoryPopoverConfig | null {
    return this.historyApiCache.get(this.getCellKey(row, column)) || null;
  }

  hasFetchedHistory(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.historyApiCache.has(this.getCellKey(row, column));
  }

  isHistoryLoading(row: MarketPositioningRow, column: MarketPositioningColumn): boolean {
    return this.historyApiLoading.has(this.getCellKey(row, column));
  }

  // fetchHistoryIfNeeded() caches per row so repeated hovers don't refire the request — but
  // that means a rating edit made after the first hover would otherwise keep showing the
  // stale pre-edit history forever. Call this after any successful save so the next hover
  // re-fetches and picks up the change.
  private invalidateHistoryCacheForRows(rows: MarketPositioningRow[]): void {
    const historyColumns = this.visibleColumns.filter((column) => column.type === 'history');
    if (historyColumns.length === 0) {
      return;
    }
    rows.forEach((row) => {
      historyColumns.forEach((column) => {
        const cellKey = this.getCellKey(row, column);
        this.historyApiCache.delete(cellKey);
        this.historyApiLoading.delete(cellKey);
      });
    });
  }

  private fetchHistoryIfNeeded(row: MarketPositioningRow, column: MarketPositioningColumn): void {
    if (!column.historyApi) {
      return;
    }
    const cellKey = this.getCellKey(row, column);
    // Deliberately NOT gated on historyApiCache.has() — every hover should hit the API fresh
    // (that's the whole point of syncBeforeFetch), not silently reuse a prior result. Only
    // guard against firing a second request while one for this exact cell is already in flight.
    if (this.historyApiLoading.has(cellKey)) {
      return;
    }
    this.historyApiLoading.add(cellKey);
    this.cdr.markForCheck();

    if (column.historyApi.syncBeforeFetch) {
      this.syncCurrentStatusBeforeHistoryFetch(row).subscribe({
        next: () => this.performHistoryFetch(row, column, cellKey),
        // Fetch history regardless of whether the sync call succeeded — a failed sync
        // shouldn't block the user from at least seeing whatever history already exists.
        error: () => this.performHistoryFetch(row, column, cellKey),
      });
      return;
    }

    this.performHistoryFetch(row, column, cellKey);
  }

  // Re-sends every rating column's CURRENT (unchanged) status through the same update
  // endpoint used for real edits. Some backends only append a history record when this
  // endpoint runs, so this exists purely to force a fresh history entry to exist before
  // reading it — it's a no-op from the user's perspective (no rating actually changes).
  private syncCurrentStatusBeforeHistoryFetch(row: MarketPositioningRow) {
    const updateConfig = this.data?.updateAction;
    const apiUrl = String(updateConfig?.apiUrl || this.data?.apiUrl || '').trim();
    const ratingUpdates = this.visibleColumns
      .filter((column) => column.type !== 'label' && column.type !== 'history')
      .map((column) => ({ row, column, status: this.getCell(row, column)?.rating || '', reason: '' }))
      .filter((update) => !!update.status);

    if (!apiUrl || ratingUpdates.length === 0) {
      return of(null);
    }

    const payload = this.buildUpdatePayload(ratingUpdates);
    return this.configService.getApi(
      apiUrl,
      { queryParams: {}, requestParams: {} },
      {
        skipDefaultPayload: true,
        customPayload: payload,
        headers: updateConfig?.headers,
      },
      updateConfig?.method || 'post',
    );
  }

  private performHistoryFetch(row: MarketPositioningRow, column: MarketPositioningColumn, cellKey: string): void {
    const filterField = column.historyApi!.filterAttributeName || 'customer_name';
    const filterValue = (row as any)?.[filterField] ?? this.getHistoryPopoverTitle(row, column);
    const payload = {
      attributesList: column.historyApi!.attributesList || ['history'],
      filterList: [
        {
          operator: column.historyApi!.filterOperator || '=',
          attributeName: filterField,
          attributeValue: filterValue,
          logicalOperator: '',
        },
      ],
      entityName: column.historyApi!.entityName,
    };
    const resolvedUrl = this.resolveHistoryApiUrl(column.historyApi!.apiUrl || '/spriced/platform');
    this.http.post(resolvedUrl, payload).subscribe({
      next: (response: any) => {
        this.historyApiLoading.delete(cellKey);
        // Real API data only — mapHistoryApiResponse() returns null for anything malformed/
        // empty/unrecognized, and getHistoryPopover() has no mock to fall back to anymore, so
        // the template shows the explicit "no history" state instead of fabricated content.
        const mapped = this.mapHistoryApiResponse(response);
        if (mapped) {
          this.historyApiCache.set(cellKey, mapped);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.historyApiLoading.delete(cellKey);
        this.cdr.markForCheck();
      },
    });
  }

  private resolveHistoryApiUrl(url: string): string {
    const trimmedUrl = String(url || '').trim();
    const isLocalAssets = trimmedUrl.startsWith('assets') || trimmedUrl.startsWith('/assets');
    const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedUrl);

    if (!trimmedUrl || isLocalAssets || isAbsoluteUrl) {
      return trimmedUrl;
    }

    const baseUrl = environment.URL.endsWith('/')
      ? environment.URL.slice(0, -1)
      : environment.URL;
    const path = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    return `${baseUrl}${path}`;
  }

  // Meta/audit columns on each snapshot's DATA object — not real business-field changes, and
  // Updated By / Updated Date are already surfaced separately in the version card header, so
  // including them here would show up as spurious "changed fields".
  private static readonly HISTORY_IGNORED_FIELDS = new Set([
    'created_by',
    'created_date',
    'updated_by',
    'updated_date',
    'version_no',
    'is_current',
    'mark_for_deletion',
  ]);

  private static readonly HISTORY_FIELD_GROUPS: Record<string, MarketPositioningHistoryFieldGroup> = {
    customersMarketShare: {
      keys: ['companies_market_share_segment', 'customers_market_share_update'],
      displayPriority: ['customers_market_share_update', 'companies_market_share_segment'],
    },
    cumminsMarketShare: {
      keys: ['cummins_share_companies_market_share_segment', 'cummins_share_of_wallet_update'],
      displayPriority: ['cummins_share_of_wallet_update', 'cummins_share_companies_market_share_segment'],
    },
    price: {
      keys: ['competition_price', 'price_update'],
      displayPriority: ['price_update', 'competition_price'],
    },
    salesforceInsights: {
      keys: ['salesforce_win_ratio', 'salesforce_insights_update'],
      displayPriority: ['salesforce_insights_update', 'salesforce_win_ratio'],
    },
    valueOffering: {
      keys: ['value_offering', 'value_offering_update'],
      displayPriority: ['value_offering_update', 'value_offering'],
    },
    strategicSignificance: {
      keys: ['segment_strategic_importance', 'strategic_significance_update'],
      displayPriority: ['strategic_significance_update', 'segment_strategic_importance'],
    },
    sales: {
      keys: ['sales'],
      displayPriority: ['sales'],
    },
    margin: {
      keys: ['margin'],
      displayPriority: ['margin'],
    },
    cagr: {
      keys: ['cagr'],
      displayPriority: ['cagr'],
    },
  };

  // The backend returns full state snapshots per version (DATA holds every field's value at
  // that point in time), not pre-computed diffs — so "what changed" has to be derived here by
  // comparing the latest snapshot against the one immediately before it.
  private mapHistoryApiResponse(response: any): MarketPositioningHistoryPopoverConfig | null {
    // Defensively unwrap whatever shape actually comes back — bare array, wrapped object, or
    // something malformed/empty — rather than assuming one exact shape and throwing.
    let entries: any = null;
    if (Array.isArray(response)) {
      entries = response[0]?.history;
    } else if (response && typeof response === 'object') {
      entries = response.history;
    }
    if (!Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const validEntries = entries.filter((entry) => entry && typeof entry === 'object' && entry.DATA);
    if (validEntries.length === 0) {
      return null;
    }

    const sorted = [...validEntries].sort((a, b) => Number(b?.Version_no ?? 0) - Number(a?.Version_no ?? 0));
    const latest = sorted[0];
    if (!latest) {
      return null;
    }

    const versions = sorted.slice(0, 3).map((entry, index) => {
      const previousEntry = sorted[index + 1];
      const diffChanges = previousEntry
        ? this.diffHistorySnapshots(previousEntry.DATA, entry.DATA)
        : this.buildSnapshotHistoryChanges(entry.DATA);
      const changes = diffChanges.length > 0 ? diffChanges : this.buildSnapshotHistoryChanges(entry.DATA);
      return {
        versionNo: Number(entry?.Version_no ?? 0),
        updatedBy: String(entry?.Updated_by ?? entry.DATA?.updated_by ?? ''),
        updatedDate: String(entry?.Updated_date ?? entry.DATA?.updated_date ?? ''),
        changes,
      };
    }).filter((version) => version.changes.length > 0);
    return {
      customerId: String(latest.DATA?.one_bms_code ?? ''),
      updatedBy: String(latest.Updated_by ?? latest.DATA?.updated_by ?? ''),
      updatedDate: String(latest.Updated_date ?? latest.DATA?.updated_date ?? ''),
      versions,
    };
  }

  private buildSnapshotHistoryChanges(data: Record<string, any>): MarketPositioningHistoryChange[] {
    const ratingColumns = this.visibleColumns.filter((column) => column.type === 'rating');
    const changes: MarketPositioningHistoryChange[] = [];

    ratingColumns.forEach((column) => {
      const group = this.getHistoryFieldGroup(column.field);
      if (!group) {
        return;
      }

      const ratingValue = this.resolveHistoryRatingValue(data, group);
      if (ratingValue === undefined) {
        return;
      }

      changes.push({
        field: String(column.header || column.field),
        previousValue: this.formatHistoryRatingValue(ratingValue),
        newValue: '-',
      });
    });

    return changes;
  }

  private diffHistorySnapshots(previousData: Record<string, any>, latestData: Record<string, any>): MarketPositioningHistoryChange[] {
    const groupedChanges = this.buildGroupedHistoryChanges(previousData, latestData);
    if (groupedChanges.length > 0) {
      return groupedChanges;
    }

    const keys = new Set([...Object.keys(previousData || {}), ...Object.keys(latestData || {})]);
    const changes: MarketPositioningHistoryChange[] = [];
    keys.forEach((key) => {
      if (MarketPositioningComponent.HISTORY_IGNORED_FIELDS.has(key)) {
        return;
      }
      const prevValue = previousData?.[key];
      const nextValue = latestData?.[key];
      if (this.normalizeHistoryValue(prevValue) === this.normalizeHistoryValue(nextValue)) {
        return;
      }
      changes.push({
        field: this.formatHistoryFieldLabel(key),
        previousValue: this.formatHistoryValue(prevValue),
        newValue: this.formatHistoryValue(nextValue),
      });
    });
    return changes;
  }

  private buildGroupedHistoryChanges(previousData: Record<string, any>, latestData: Record<string, any>): MarketPositioningHistoryChange[] {
    const ratingColumns = this.visibleColumns.filter((column) => column.type === 'rating');
    if (!ratingColumns.length) {
      return [];
    }

    const changes: MarketPositioningHistoryChange[] = [];
    ratingColumns.forEach((column) => {
      const group = this.getHistoryFieldGroup(column.field);
      if (!group) {
        return;
      }

      const previousRating = this.resolveHistoryRatingValue(previousData, group);
      const latestRating = this.resolveHistoryRatingValue(latestData, group);
      const previousLabel = this.formatHistoryRatingValue(previousRating);
      const latestLabel = this.formatHistoryRatingValue(latestRating);

      if (previousLabel === latestLabel) {
        return;
      }

      changes.push({
        field: String(column.header || column.field),
        previousValue: previousLabel,
        newValue: latestLabel,
      });
    });

    return changes;
  }

  private resolveHistoryRatingValue(data: Record<string, any>, group: MarketPositioningHistoryFieldGroup): any {
    const preferredKey = group.displayPriority.find((key) => this.isMeaningfulHistoryRatingValue(data?.[key]));
    if (preferredKey) {
      return data?.[preferredKey];
    }

    const fallbackKey = group.keys.find((key) => this.isMeaningfulHistoryRatingValue(data?.[key]));
    return fallbackKey ? data?.[fallbackKey] : undefined;
  }

  private isMeaningfulHistoryRatingValue(value: any): boolean {
    const normalized = this.normalizeHistoryValue(value).trim();
    if (!normalized) {
      return false;
    }

    const lower = normalized.toLowerCase();
    if (['good', 'fair', 'weak'].includes(lower)) {
      return true;
    }

    return Number.isFinite(Number(normalized));
  }

  private getHistoryFieldGroup(columnField: string): MarketPositioningHistoryFieldGroup | null {
    const key = String(columnField || '').trim();
    return MarketPositioningComponent.HISTORY_FIELD_GROUPS[key] || null;
  }

  private normalizeHistoryValue(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }

  private formatHistoryValue(value: any): string {
    const normalized = this.normalizeHistoryValue(value);
    return normalized === '' ? '-' : normalized;
  }

  private formatHistoryRatingValue(value: any): string {
    const normalized = this.normalizeHistoryValue(value);
    if (normalized === '') {
      return '-';
    }

    const ratingKey = normalized.toLowerCase();
    if (['good', 'fair', 'weak'].includes(ratingKey)) {
      return this.getRatingConfig(ratingKey).label || this.toTitleCase(ratingKey);
    }

    const numericValue = Number(normalized);
    if (Number.isFinite(numericValue)) {
      if (numericValue <= 4) {
        return this.getRatingConfig('weak').label || 'Weak';
      }

      if (numericValue <= 6) {
        return this.getRatingConfig('fair').label || 'Fair';
      }

      return this.getRatingConfig('good').label || 'Good';
    }

    return normalized;
  }

  private toTitleCase(value: string): string {
    return String(value || '').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private formatHistoryFieldLabel(key: string): string {
    const mappedColumnField = this.resolveHistoryFieldColumn(key);
    if (mappedColumnField) {
      const matchingColumn = this.visibleColumns.find((column) => column.field === mappedColumnField);
      const header = String(matchingColumn?.header || '').trim();
      if (header) {
        return header;
      }
    }

    return key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private resolveHistoryFieldColumn(key: string): string | null {
    const normalized = String(key || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s+update$/, '');

    if (!normalized) {
      return null;
    }

    if (
      normalized.includes('customers market share') ||
      normalized.includes('companies market share')
    ) {
      return 'customersMarketShare';
    }

    if (normalized.includes('cummins share')) {
      return 'cumminsMarketShare';
    }

    if (
      normalized === 'price' ||
      normalized.includes('competition price')
    ) {
      return 'price';
    }

    if (normalized.includes('salesforce')) {
      return 'salesforceInsights';
    }

    if (normalized.includes('value offering')) {
      return 'valueOffering';
    }

    if (
      normalized.includes('strategic significance') ||
      normalized.includes('segment strategic importance')
    ) {
      return 'strategicSignificance';
    }

    return null;
  }

  getHistoryPopoverTitle(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    const field = column.historyPopoverTitleField;
    const value = field ? (row as any)?.[field] : undefined;
    return value !== undefined && value !== null && value !== '' ? String(value) : 'History';
  }

  onHistoryMouseEnter(row: MarketPositioningRow, column: MarketPositioningColumn, event: MouseEvent): void {
    if (!column.historyApi) {
      return;
    }
    if (this.pinnedHistoryPopoverCellKey) {
      return;
    }
    this.hoverPopoverCellKey = this.getCellKey(row, column);
    this.fetchHistoryIfNeeded(row, column);
    this.positionHistoryPopover(event.currentTarget as HTMLElement);
    this.cdr.markForCheck();
  }

  onHistoryClick(row: MarketPositioningRow, column: MarketPositioningColumn, event: MouseEvent): void {
    event.stopPropagation();
    if (!column.historyApi) {
      return;
    }

    const key = this.getCellKey(row, column);
    if (this.pinnedHistoryPopoverCellKey === key) {
      this.closeHoverPopover();
      return;
    }

    this.pinnedHistoryPopoverCellKey = key;
    this.hoverPopoverCellKey = key;
    this.fetchHistoryIfNeeded(row, column);
    this.positionHistoryPopover(event.currentTarget as HTMLElement);
    this.cdr.markForCheck();
  }

  private positionHistoryPopover(wrapper: HTMLElement): void {
    const anchor = wrapper?.getBoundingClientRect();
    if (anchor) {
      // position:fixed anchored to the icon's viewport rect, capped to the space actually
      // available below the table's own top edge — see json-driven-smart-table's identical
      // mechanism for why: clamping position alone isn't enough, since content taller than
      // the available room would still run off the bottom of the screen either way.
      const margin = 12;
      const tableTop = wrapper.closest('.market-positioning__table-shell')?.getBoundingClientRect().top ?? margin;
      const minTop = Math.max(margin, tableTop);
      this.hoverPopoverMaxHeight = Math.max(160, window.innerHeight - minTop - margin);
      this.hoverPopoverPosition = {
        top: minTop,
        right: window.innerWidth - anchor.left + 14,
      };
      if (typeof window !== 'undefined') {
        window.setTimeout(() => this.recenterHoverPopover(anchor, minTop, margin), 0);
      }
    }
  }

  onHistoryMouseLeave(row: MarketPositioningRow, column: MarketPositioningColumn): void {
    if (!column.historyApi) {
      return;
    }
    const key = this.getCellKey(row, column);
    if (this.pinnedHistoryPopoverCellKey === key) {
      return;
    }
    if (this.hoverPopoverCellKey === key) {
      this.closeHoverPopover();
    }
  }

  closeHoverPopover(): void {
    this.hoverPopoverCellKey = null;
    this.pinnedHistoryPopoverCellKey = null;
    this.hoverPopoverPosition = null;
    this.hoverPopoverMaxHeight = null;
    this.cdr.markForCheck();
  }

  private recenterHoverPopover(anchor: DOMRect, minTop: number, margin: number): void {
    if (!this.hoverPopoverPosition) {
      return;
    }
    const el = document.querySelector('.cell-hover-popover') as HTMLElement | null;
    if (!el) {
      return;
    }
    const height = el.getBoundingClientRect().height;
    const maxTop = Math.max(minTop, window.innerHeight - height - margin);
    const desiredTop = anchor.top + anchor.height / 2 - height / 2;
    const clampedTop = Math.min(Math.max(desiredTop, minTop), maxTop);
    if (Math.abs(clampedTop - this.hoverPopoverPosition.top) > 0.5) {
      this.hoverPopoverPosition = { ...this.hoverPopoverPosition, top: clampedTop };
      this.cdr.markForCheck();
    }
  }

  isLatestHistoryVersion(popover: MarketPositioningHistoryPopoverConfig, version: MarketPositioningHistoryVersion): boolean {
    return popover.versions[0] === version;
  }

  isHistoryVersionExpanded(column: MarketPositioningColumn, version: MarketPositioningHistoryVersion): boolean {
    return !this.collapsedHistoryVersionKeys.has(this.buildHistoryVersionKey(column, version));
  }

  toggleHistoryVersion(column: MarketPositioningColumn, version: MarketPositioningHistoryVersion, event?: Event): void {
    event?.stopPropagation();
    const key = this.buildHistoryVersionKey(column, version);
    if (this.collapsedHistoryVersionKeys.has(key)) {
      this.collapsedHistoryVersionKeys.delete(key);
    } else {
      this.collapsedHistoryVersionKeys.add(key);
    }
    this.cdr.markForCheck();
  }

  private buildHistoryVersionKey(column: MarketPositioningColumn, version: MarketPositioningHistoryVersion): string {
    return `${column.field}::${version.versionNo}`;
  }

  formatHistoryDate(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }
    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }

  private buildUpdatePayload(
    updates: Array<{ row: MarketPositioningRow; column: MarketPositioningColumn; status: string; reason?: string }>
  ): Record<string, any> {
    const updateConfig = this.data?.updateAction || {};
    const requestTemplate = this.toPlainObject(updateConfig.requestTemplate);
    const requestDataTemplate = this.toPlainObject(requestTemplate['requestData']);
    const columnNameMap = this.toPlainObject(updateConfig.columnNameMap);
    const customerField = String(updateConfig.customerField || this.data?.apiMapping?.rowLabelField || 'customer_name');

    return {
      ...requestTemplate,
      requestData: {
        ...requestDataTemplate,
        updates: updates.map(({ row, column, status, reason }) => ({
          customer_name: String(
            this.resolvePath(row, customerField) ??
            row['customer_name'] ??
            this.getCellLabel(row, this.visibleColumns[0])
          ),
          one_bms_code: String(
            this.resolvePath(row, 'one_bms_code') ??
            row['one_bms_code'] ??
            ''
          ),
          column_name: String(columnNameMap[column.field] || column.field),
          status: String(status || '').toLowerCase(),
          reason: String(reason || '').trim(),
        })),
      },
      // Top-level, sibling to requestData/endpoints — not nested per-update — matching the
      // envelope shape this backend expects, same as the userId this endpoint's requestTemplate
      // otherwise leaves out entirely.
      userId: this.resolveUserTemplate(updateConfig.userId),
    };
  }

  private commitSavedCells(
    updates: Array<{ row: MarketPositioningRow; column: MarketPositioningColumn; status: string; reason?: string; cellKey: string }>
  ): void {
    updates.forEach(({ row, column, status, reason, cellKey }) => {
      const originalRow = this.originalRows.find((item) => item.id === row.id);
      const originalCell = originalRow ? this.getCell(originalRow, column) : null;
      if (originalCell) {
        originalCell.rating = status;
        originalCell.update = this.getLocalUpdatedLabel();
        originalCell.reason = this.getDisplayText(reason || this.editReasons[cellKey]) || null;
      }

      const currentCell = this.getCell(row, column);
      if (currentCell) {
        currentCell.update = this.getLocalUpdatedLabel();
        currentCell.reason = this.getDisplayText(reason || this.editReasons[cellKey]) || null;
      }

      this.pendingCellChanges.delete(this.getCellKey(row, column));
      this.persistSavedRating(row, column, status);
      const savedReason = String(reason || this.editReasons[cellKey] || '').trim();
      if (savedReason) {
        this.savedEditReasons[cellKey] = savedReason;
      } else {
        delete this.savedEditReasons[cellKey];
      }

      this.updatedCellKeys.add(cellKey);
      delete this.editReasons[cellKey];
      if (this.openReasonEditorCellKey === cellKey) {
        this.openReasonEditorCellKey = null;
      }

      this.pendingCellChanges.delete(cellKey);
    });
  }

  private collectPendingUpdates(): Array<{
    row: MarketPositioningRow;
    column: MarketPositioningColumn;
    status: string;
    reason?: string;
    cellKey: string;
  }> {
    const updates: Array<{ row: MarketPositioningRow; column: MarketPositioningColumn; status: string; reason?: string; cellKey: string }> = [];

    this.rows.forEach((row) => {
      this.visibleColumns
        .filter((column) => column.type !== 'label' && column.editable)
        .forEach((column) => {
          const cellKey = this.getCellKey(row, column);
          if (!this.pendingCellChanges.has(cellKey)) {
            return;
          }

          const cell = this.getCell(row, column);
          if (!cell) {
            return;
          }

          updates.push({
            row,
            column,
            status: String(cell.rating || '').toLowerCase(),
            reason: this.editReasons[cellKey] || '',
            cellKey,
          });
        });
    });

    return updates;

  }

  private rowHasRating(row: MarketPositioningRow, rating: string): boolean {
    return this.visibleColumns.some((column) => {
      const cell = this.getCell(row, column);
      return cell?.rating === rating;
    });
  }

  private cloneRows(rows: MarketPositioningRow[]): MarketPositioningRow[] {
    return JSON.parse(JSON.stringify(rows || []));
  }

  private buildCsv(columns: MarketPositioningColumn[], rows: MarketPositioningRow[]): string {
    const header = columns.map((column) => this.escapeCsvValue(column.header));
    const body = rows.map((row) =>
      columns.map((column) => this.escapeCsvValue(this.getExportCellValue(row, column))).join(','),
    );

    return [header.join(','), ...body].join('\r\n');
  }

  private getExportCellValue(row: MarketPositioningRow, column: MarketPositioningColumn): string {
    if (column.type === 'label') {
      return this.getCellLabel(row, column);
    }

    const cell = this.getCell(row, column);
    if (!cell) {
      return String(row[column.field] ?? '');
    }

    const ratingLabel = this.getRatingConfig(cell.rating).label || cell.rating;
    if (!this.shouldShowProgress(cell)) {
      return ratingLabel;
    }

    return `${ratingLabel} (${cell.confidence}${this.data?.table?.progress?.suffix || ''})`;
  }

  private escapeCsvValue(value: unknown): string {
    const text = String(value ?? '');
    const escaped = text.replace(/"/g, '""');
    return /[",\r\n]/.test(escaped) ? `"${escaped}"` : escaped;
  }

  private resolveExportFileName(): string {
    const configuredName = String(
      this.data?.actions?.export?.fileName ||
      this.data?.actions?.export?.filename ||
      this.data?.exportFileName ||
      'market-positioning-configuration.csv',
    ).trim();
    const withExtension = configuredName.toLowerCase().endsWith('.csv')
      ? configuredName
      : `${configuredName}.csv`;

    return withExtension.replace(/[\\/:*?"<>|]+/g, '-');
  }

  private getDisplayText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private formatDisplayLabel(value: any): string {
    const text = this.getDisplayText(value);
    if (!text) {
      return '';
    }

    if (text === text.toUpperCase()) {
      const lowerText = text.toLowerCase();
      return lowerText.charAt(0).toUpperCase() + lowerText.slice(1);
    }

    return text;
  }

  private getLocalUpdatedLabel(): string {
    return this.getDisplayText(this.data?.editReason?.updatedLabel) || 'Updated';
  }

  private applySavedRatings(rows: MarketPositioningRow[]): MarketPositioningRow[] {
    const savedRatings = this.readSavedRatings();
    if (!rows.length || Object.keys(savedRatings).length === 0) {
      return rows;
    }

    rows.forEach((row) => {
      this.visibleColumns
        .filter((column) => column.type !== 'label')
        .forEach((column) => {
          const cell = this.getCell(row, column);
          if (!cell) {
            return;
          }

          const savedRating = savedRatings[this.getCellKey(row, column)];
          if (savedRating) {
            cell.rating = savedRating;
          }
        });
    });

    return rows;
  }

  private persistSavedRating(row: MarketPositioningRow, column: MarketPositioningColumn, rating: string): void {
    const savedRatings = this.readSavedRatings();
    savedRatings[this.getCellKey(row, column)] = rating;
    this.writeSavedRatings(savedRatings);
  }

  private readSavedRatings(): Record<string, string> {
    const storageKey = this.getSavedRatingsStorageKey();
    if (!storageKey) {
      return {};
    }

    try {
      const rawValue = window.localStorage.getItem(storageKey);
      if (!rawValue) {
        return {};
      }

      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
            const normalizedValue = String(value || '').trim();
            if (normalizedValue) {
              acc[key] = normalizedValue;
            }
            return acc;
          }, {})
        : {};
    } catch {
      return {};
    }
  }

  private writeSavedRatings(savedRatings: Record<string, string>): void {
    const storageKey = this.getSavedRatingsStorageKey();
    if (!storageKey) {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(savedRatings));
    } catch {
      // Ignore storage failures and continue with in-memory state.
    }
  }

  private getSavedRatingsStorageKey(): string {
    const widgetId = String(this.data?.widgetId || '').trim();
    return widgetId
      ? `${MarketPositioningComponent.SAVED_RATINGS_STORAGE_PREFIX}.${widgetId}`
      : '';
  }

  private emitRows(): void {
    this.rowsChanged.emit(this.cloneRows(this.rows));
    this.cdr.markForCheck();
  }

  private showSuccessMessage(): void {
    this.clearSuccessMessageTimer();
    this.successMessage = String(
      this.data?.messages?.updateSuccess ||
      this.data?.messages?.successMessage ||
      'Saved successfully.',
    );
    this.successMessageTimer = setTimeout(() => {
      this.successMessage = '';
      this.successMessageTimer = undefined;
      this.cdr.markForCheck();
    }, Number(this.data?.messages?.successMessageDurationMs) || 3000);
    this.cdr.markForCheck();
  }

  private clearSuccessMessage(): void {
    this.clearSuccessMessageTimer();
    this.successMessage = '';
  }

  private clearSuccessMessageTimer(): void {
    if (!this.successMessageTimer) {
      return;
    }

    clearTimeout(this.successMessageTimer);
    this.successMessageTimer = undefined;
  }
}
